<template>
  <div>
    <b-modal
      v-model="showModal"
      :title="`Detail ${selectedRole?.title || ''} (${selectedSubCategory?.name || ''})`"
      size="xl"
      hide-footer
      modal-class="detail-modal"
      @hide="closeModal"
    >
    <template #modal-header="{ close }">
      <h5 class="modal-title mb-0">
        Detail {{ selectedRole?.title || '' }} ({{ selectedSubCategory?.name || '' }})
      </h5>
      <button type="button" class="close" aria-label="Close" @click="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </template>
    <div class="detail-modal-content">
      <!-- Header Section -->
      <div class="modal-header-section">
        <div class="download-wrapper">
          <span class="download-label">Download Report</span>
          <b-button variant="danger" :disabled="isExporting" @click="downloadReport">
            <i class="fe fe-download"></i>
            {{ isExporting ? 'Exporting...' : 'Report' }}
          </b-button>
        </div>
      </div>

      <!-- Company Info -->
      <!-- <div class="company-section">
        <div class="company-label">Company</div>
        <div class="company-value">{{ companyName }}</div>
      </div> -->

      <!-- Summary Stats -->
      <!-- <div class="stats-container">
        <div class="stats-grid">
          <div class="stat-column">
            <div class="stat-label">Plan</div>
            <div class="stat-value">{{ detailData.plan }}</div>
          </div>
          <div class="stat-column">
            <div class="stat-label">Actual</div>
            <div class="stat-value">{{ detailData.actual }}</div>
          </div>
          <div class="stat-column">
            <div class="stat-label">Goals</div>
            <div class="stat-value">{{ detailData.goals }}%</div>
          </div>
        </div>
      </div> -->

      <!-- Table Section -->
      <div class="table-section">
        <!-- Competency Filter -->
        <div class="competency-filter mb-3">
          <div class="row align-items-center">
            <div class="col-auto">
              <label class="mb-0 font-weight-bold">Filter Course Catagory:</label>
            </div>
            <div class="col-md-4">
              <v-select
                v-model="selectedCompetencies"
                :options="competencyOptions"
                :reduce="option => option.code"
                label="label"
                placeholder="All Competencies"
                class="competency-select"
              >
                <template #option="{ code, label }">
                  <strong>{{ code }}</strong> - {{ label }}
                </template>
              </v-select>
            </div>
            <div class="col-auto" v-if="selectedCompetencies">
              <b-button variant="outline-primary" size="sm" @click="clearFilter">
                Clear Filter
              </b-button>
            </div>
          </div>
        </div>

        <div class="table-scroll-wrapper">
          <!-- Loading Indicator -->
          <div v-if="isLoading" class="text-center py-5">
            <b-spinner variant="primary"></b-spinner>
            <p class="mt-2">Loading company data...</p>
          </div>

          <!-- Table -->
          <table v-else class="table detail-table">
          <thead>
            <tr>
              <th class="text-center" rowspan="3" style="vertical-align: middle;">NO</th>
              <th rowspan="3" style="vertical-align: middle;">COMPANY NAME</th>
              <th v-if="filteredCompetencyTypes.length > 0" class="text-center" :colspan="filteredCompetencyTypes.length * 3">COURSES</th>
              <th class="text-center" rowspan="3" style="vertical-align: middle;">TOTAL</th>
              <th class="text-center" rowspan="3" style="vertical-align: middle;">ACTION</th>
            </tr>
            <tr>
              <template v-for="competency in filteredCompetencyTypes">
                <th class="text-center" colspan="2" :key="competency.code">{{ competency.code }}</th>
                <th class="text-center" rowspan="2" :key="competency.code + '-goals'" style="vertical-align: middle;">GOALS</th>
              </template>
            </tr>
            <tr>
              <template v-for="competency in filteredCompetencyTypes">
                <th class="text-center" :key="competency.code + '-plan'">PLAN</th>
                <th class="text-center" :key="competency.code + '-actual'">ACTUAL</th>
              </template>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!isLoading && paginatedCompanies.length === 0">
              <td :colspan="filteredCompetencyTypes.length * 3 + 3" class="text-center py-4">
                No data available
              </td>
            </tr>
            <tr v-for="(company, index) in paginatedCompanies" :key="index">
              <td class="text-center">{{ (currentPage - 1) * perPage + index + 1 }}</td>
              <td>{{ company.name }}</td>
              <template v-for="competency in filteredCompetencyTypes">
                <td class="text-center" :key="competency.code + '-plan-' + index">{{ company.competencies[competency.code]?.plan || 0 }}</td>
                <td class="text-center" :key="competency.code + '-actual-' + index">{{ company.competencies[competency.code]?.actual || 0 }}</td>
                <td class="text-center" :key="competency.code + '-goals-' + index">{{ company.competencies[competency.code]?.goals || 0 }}%</td>
              </template>
              <td class="text-center">{{ company.total }}%</td>
              <td class="text-center">
                <button class="btn btn-info btn-sm" @click="viewCompanyDetail(company)">
                  <i class="fe fe-eye"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper">
          <nav>
            <ul class="pagination pagination-sm mb-0">
              <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage - 1)">‹</a>
              </li>
              <li 
                v-for="page in visiblePages" 
                :key="page" 
                class="page-item" 
                :class="{ active: currentPage === page, disabled: page === '...' }"
              >
                <a 
                  v-if="page === '...'" 
                  class="page-link" 
                  href="#"
                  @click.prevent>
                  ...
                </a>
                <a 
                  v-else
                  class="page-link" 
                  href="#" 
                  @click.prevent="changePage(page)">
                  {{ page }}
                </a>
              </li>
              <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="changePage(currentPage + 1)">›</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </b-modal>

    <!-- Employee Detail Modal -->
    <employee-detail-modal
      :visible="showEmployeeModal"
      :company-name="selectedCompany?.name || ''"
      :company-data="selectedCompany"
      :selected-role="selectedRole"
      :date-range="dateRange"
      :selected-competencies="selectedCompetencies"
      :is-h-s-e-training-role="isHSETrainingRole"
      :is-employee-role="isEmployeeRole"
      :is-manager-role="isManagerRole"
      :is-h-r-operations-site-role="isHROperationsSiteRole"
      :is-h-r-operations-jakarta-role="isHROperationsJakartaRole"
      :kplh-type="kplhType"
      :training-type="trainingType"
      :is-partner-role="isPartnerRole"
      @close="closeEmployeeModal"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import EmployeeDetailModal from './EmployeeDetailModal.vue';
import { 
  GET_DASHBOARD_ROLE_COMPETENCIES_PARTNER_DETAIL,
  GET_DASHBOARD_ROLE_COMPETENCIES_HSE_DETAIL,
  GET_DASHBOARD_ROLE_COMPETENCIES_SITE_DETAIL,
  GET_DASHBOARD_ROLE_COMPETENCIES_INTERNAL_DETAIL,
  GET_DASHBOARD_COMPETENCIES_INTERNAL_DETAIL 
} from '@/api/dashboard/dashboard';
import moment from 'moment';
import {
  exportCompanyMatrixExcel,
  buildDashboardReportFilename,
  formatReportPeriod,
  formatTrainingTypeLabel
} from '@/utils/dashboardExcelExport';

export default {
  name: "DetailModal",
  components: {
    EmployeeDetailModal
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    selectedRole: {
      type: Object,
      default: null
    },
    selectedSubCategory: {
      type: Object,
      default: null
    },
    companyName: {
      type: String,
      default: ""
    },
    dateRange: {
      type: Object,
      default: () => ({
        start: null,
        end: null
      })
    },
    isHROperationsSiteRole: {
      type: Boolean,
      default: false
    },
    isHSETrainingRole: {
      type: Boolean,
      default: false
    },
    isEmployeeRole: {
      type: Boolean,
      default: false
    },
    isManagerRole: {
      type: Boolean,
      default: false
    },
    isPartnerRole: {
      type: Boolean,
      default: false
    },
    isHROperationsJakartaRole: {
      type: Boolean,
      default: false
    },
    kplhType: {
      type: String,
      default: 'KPLH'
    },
    trainingType: {
      type: String,
      default: 'all'
    }
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      showEmployeeModal: false,
      selectedCompany: null,
      selectedCompetencies: null, // Filter for competency types
      isLoading: false,
      isExporting: false,
      // Company data from API
      companyList: []
    };
  },
  computed: {
    ...mapState('trainingCategories', ['trainingCategories']),
    competencyTypes() {
      return (Array.isArray(this.trainingCategories) ? this.trainingCategories : []).map(category => ({
        code: category.abbreviation,
        name: category.title,
        id: category.id
      }));
    },
    showModal: {
      get() {
        return this.visible;
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      }
    },
    competencyOptions() {
      return this.competencyTypes.map(comp => ({
        code: comp.code,
        label: `${comp.code} - ${comp.name}`
      }));
    },
    isEmployeeOrManagerRole() {
      return this.isEmployeeRole || this.isManagerRole;
    },
    filteredCompetencyTypes() {
      // Get all unique training names from company data
      const allTrainings = new Set();
      this.companyList.forEach(company => {
        if (company.competencies) {
          Object.keys(company.competencies).forEach(trainingName => {
            allTrainings.add(trainingName);
          });
        }
      });

      // Map to training objects with their details
      let trainings = Array.from(allTrainings).map(trainingName => {
        // Get training_category_id from first company's competency data
        const firstCompany = this.companyList.find(c => c.competencies && c.competencies[trainingName]);
        const training_category_id = firstCompany?.competencies[trainingName]?.training_category_id;
        
        return {
          code: trainingName, // Using training name as code for display
          name: trainingName,
          id: training_category_id
        };
      });

      // If filter selected, filter by training_category_id
      if (this.selectedCompetencies) {
        const selectedComp = this.competencyTypes.find(ct => ct.code === this.selectedCompetencies);
        if (selectedComp) {
          trainings = trainings.filter(training => training.id === selectedComp.id);
        }
      }

      return trainings;
    },
    detailData() {
      if (this.selectedSubCategory) {
        return {
          plan: this.selectedSubCategory.plan || 0,
          actual: this.selectedSubCategory.actual || 0,
          goals: this.selectedSubCategory.percentage || this.selectedSubCategory.goals || 0
        };
      }
      return {
        plan: 0,
        actual: 0,
        goals: 0
      };
    },
    paginatedCompanies() {
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return this.companyList.slice(start, end);
    },
    totalPages() {
      return Math.ceil(this.companyList.length / this.perPage);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      if (total <= 7) {
        for (let i = 1; i <= total; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        
        if (current > 3) {
          pages.push('...');
        }
        
        const start = Math.max(2, current - 1);
        const end = Math.min(total - 1, current + 1);
        
        for (let i = start; i <= end; i++) {
          pages.push(i);
        }
        
        if (current < total - 2) {
          pages.push('...');
        }
        
        pages.push(total);
      }
      
      return pages.length > 0 ? pages : [1];
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.currentPage = 1; // Reset to first page when modal opens
        this.fetchTrainingCategoriesData();
        this.fetchCompanyData(); // Fetch company data when modal opens
      }
    },
    selectedCompetencies() {
      // Refetch data when filter changes
      if (this.visible) {
        this.fetchCompanyData();
      }
    },
    trainingType() {
      if (this.visible) {
        this.currentPage = 1;
        this.fetchCompanyData();
      }
    }
  },
  mounted() {
    this.fetchTrainingCategoriesData();
  },
  methods: {
    ...mapActions('trainingCategories', ['fetchTrainingCategories']),
    async fetchTrainingCategoriesData() {
      try {
        if (Array.isArray(this.trainingCategories) && this.trainingCategories.length > 0) return;
        await this.fetchTrainingCategories({ limit: '', search: '' });
      } catch (error) {
        console.error('Error fetching training categories:', error);
      }
    },
    async fetchCompanyData() {
      if (!this.selectedRole?.id) {
        console.error('No role competency ID provided');
        return;
      }

      this.isLoading = true;
      this.companyList = [];
      
      try {
        const params = {
          start_date: this.dateRange.start ? moment(this.dateRange.start).format('MM/DD/YYYY') : null,
          end_date: this.dateRange.end ? moment(this.dateRange.end).format('MM/DD/YYYY') : null,
          training_type: this.trainingType || 'all'
        };

        if (this.companyName) {
          params.company = this.companyName;
        }

        // Add training_category_id filter if selected
        if (this.selectedCompetencies) {
          // Get the training_category_id from the selected competency code
          const selectedComp = this.competencyTypes.find(ct => ct.code === this.selectedCompetencies);
          if (selectedComp) {
            params.training_category_id = selectedComp.id;
          }
        }

        // Use appropriate API based on role
        let response;
        
        // GET /dashboard/competencies/internal/:id?company=BIB&training_type=
        if ((this.isHROperationsSiteRole || this.isHROperationsJakartaRole || this.isEmployeeOrManagerRole) && this.kplhType === 'NON_KPLH') {
          params.matrix_kplh = 'NON_KPLH';
          
          // Use selectedCompetencies ID if filtered, otherwise use selectedRole.id
          const competencyId = this.selectedCompetencies 
            ? (this.competencyTypes.find(ct => ct.code === this.selectedCompetencies)?.id || this.selectedRole.id)
            : this.selectedRole.id;
          
          response = await GET_DASHBOARD_COMPETENCIES_INTERNAL_DETAIL(
            competencyId,
            params
          );
        } else if (this.isPartnerRole) {
          // GET /dashboard/role-competencies/partner/:id?training_type=
          const { company, ...partnerParams } = params;
          response = await GET_DASHBOARD_ROLE_COMPETENCIES_PARTNER_DETAIL(
            this.selectedRole.id,
            partnerParams
          );
        } else if (this.isHSETrainingRole || this.isEmployeeOrManagerRole) {
          // GET /dashboard/role-competencies/hse/:id?training_type=
          response = await GET_DASHBOARD_ROLE_COMPETENCIES_HSE_DETAIL(
            this.selectedRole.id,
            params
          );
        } else if (this.isHROperationsSiteRole) {
          // GET /dashboard/role-competencies/site/:id?training_type=
          response = await GET_DASHBOARD_ROLE_COMPETENCIES_SITE_DETAIL(
            this.selectedRole.id,
            params
          );
        } else {
          // GET /dashboard/role-competencies/internal/:id?training_type=
          response = await GET_DASHBOARD_ROLE_COMPETENCIES_INTERNAL_DETAIL(
            this.selectedRole.id,
            params
          );
        }

        const rows = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (rows.length > 0) {
          // Transform API response to match component structure
          this.companyList = rows.map(company => {
            const competencies = {};

            // Transform trainings object to competencies structure
            if (company.trainings && typeof company.trainings === 'object') {
              Object.keys(company.trainings).forEach(trainingName => {
                const training = company.trainings[trainingName];

                competencies[trainingName] = {
                  plan: training.plan,
                  actual: training.actual,
                  goals: training.goals,
                  training_category_id: training.training_category_id
                };
              });
            }

            return {
              name: company.name,
              competencies: competencies,
              total: parseFloat(company.total) || 0
            };
          });
        } else {
          this.companyList = [];
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        // Show error toast or notification
        this.$bvToast.toast('Data perusahaan belum berhasil dimuat', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isLoading = false;
      }
    },
    closeModal() {
      this.companyList = [];
      this.selectedCompetencies = null;
      this.currentPage = 1;
      this.$emit("close");
    },
    clearFilter() {
      this.selectedCompetencies = null;
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
      }
    },
    courseCategoryFilterLabel() {
      if (!this.selectedCompetencies) {
        return 'All Competencies';
      }
      const option = this.competencyOptions.find((item) => item.code === this.selectedCompetencies);
      return option?.label || this.selectedCompetencies;
    },
    async downloadReport() {
      if (this.isExporting) return;

      this.isExporting = true;
      try {
        const companyLabel = this.companyName || 'All_company';
        const categoryLabel = this.selectedCompetencies || 'All';
        await exportCompanyMatrixExcel({
          sheetName: 'Dashboard Detail',
          filename: buildDashboardReportFilename([companyLabel, categoryLabel]),
          metaRows: [
            { label: 'Report', value: 'Dashboard Detail' },
            { label: 'Role Competency', value: this.selectedRole?.title || '-' },
            { label: 'Sub Category', value: this.selectedSubCategory?.name || '-' },
            { label: 'Company / Mitra', value: this.companyName || 'All Company' },
            { label: 'Course Category', value: this.courseCategoryFilterLabel() },
            { label: 'Training Type', value: formatTrainingTypeLabel(this.trainingType) },
            { label: 'Period', value: formatReportPeriod(this.dateRange) }
          ],
          courses: this.filteredCompetencyTypes,
          companies: this.companyList
        });
        this.$bvToast.toast('Excel report downloaded', {
          title: 'Success',
          variant: 'success',
          solid: true
        });
      } catch (error) {
        console.error('Error exporting dashboard detail report:', error);
        this.$bvToast.toast('Failed to export Excel report', {
          title: 'Error',
          variant: 'danger',
          solid: true
        });
      } finally {
        this.isExporting = false;
      }
    },
    viewCompanyDetail(company) {
      this.selectedCompany = company;
      this.showEmployeeModal = true;
    },
    closeEmployeeModal() {
      this.showEmployeeModal = false;
      this.selectedCompany = null;
    }
  }
};
</script>

<style scoped>
.detail-modal >>> .modal-dialog {
  max-width: 90%;
}

.detail-modal >>> .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #fff;
  border-bottom: 1px solid #dee2e6;
  padding: 1rem 1.5rem;
}

.detail-modal >>> .modal-title {
  font-size: 1rem;
  font-weight: 600;
  color: #000;
}

.detail-modal > .modal-body {
  padding: 0;
}

/* Header Section */
.modal-header-section {
  margin-bottom: 1.5rem;
}

.download-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.download-label {
  font-size: 1rem;
  font-weight: 700;
  color: #000;
}

.download-wrapper .btn-danger {
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.download-wrapper .btn-danger i {
  margin-right: 0.5rem;
}

/* Company Section */
.company-section {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e0e0e0;
}

.company-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #000;
  margin-bottom: 0.5rem;
}

.company-value {
  font-size: 0.875rem;
  color: #000;
  font-weight: 400;
}

/* Stats Container */
.stats-container {
  margin-bottom: 2rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
}

.stat-column {
  display: flex;
  flex-direction: column;
}

.stat-label {
  font-weight: 700;
  color: #000;
  margin-bottom: 0.5rem;
}

/* Table Section */
.table-section {
  margin-top: 1.5rem;
}

/* Competency Filter */
.competency-filter {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.competency-filter label {
  font-size: 0.875rem;
  color: #333;
}

.competency-select {
  font-size: 0.875rem;
}

.table-scroll-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.table-scroll-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-scroll-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.table-scroll-wrapper::-webkit-scrollbar-thumb:hover {
  background: #555;
}

.table-title {
  font-size: 0.875rem;
  font-weight: 400;
  color: #9e9e9e;
  margin-bottom: 0.75rem;
  text-align: center;
}

.detail-table {
  width: 100%;
  margin-bottom: 1rem;
  font-size: 0.75rem;
  border-collapse: collapse;
}

.detail-table thead th {
  background-color: #fafafa;
  color: #9e9e9e;
  font-weight: 400;
  text-transform: uppercase;
  padding: 0.75rem 0.5rem;
  border: 1px solid #e0e0e0;
  font-size: 0.65rem;
  letter-spacing: 0.3px;
}

.detail-table thead th.sub-header {
  background-color: #fff;
  color: #9e9e9e;
  font-size: 0.6rem;
}

.detail-table tbody td {
  padding: 1rem 0.5rem;
  border: 1px solid #e0e0e0;
  color: #000;
  background-color: #fff;
  font-size: 0.75rem;
}

.detail-table tbody tr:hover {
  background-color: transparent;
}

/* Pagination */
.pagination-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
}

.pagination {
  display: flex;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.page-item {
  margin: 0;
}

.page-link {
  padding: 0.375rem 0.75rem;
  font-size: 0.875rem;
  color: #333;
  background-color: #fff;
  border: 1px solid #dee2e6;
  text-decoration: none;
  margin: 0 2px;
  border-radius: 3px;
}

.page-item.active .page-link {
  background-color: #B71C1C;
  border-color: #B71C1C;
  color: #fff;
}

.page-item.disabled .page-link {
  color: #6c757d;
  pointer-events: none;
  background-color: #fff;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #B71C1C;
  background-color: #f8f9fa;
  border-color: #dee2e6;
}

.page-item.active .page-link:hover {
  color: #fff;
  background-color: #B71C1C;
}
</style>
